import type { CompRef, AppController } from '@wix/document-services-types';
import { AppControllerRef, Connection } from '@wix/document-services-types';
import { AbstractComponentsPlatformService } from '../AbstractComponentsPlatformService';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../../errors';
import { BindingKey, BindingKeyManager } from '@wix/binding-key-manager';
import type { EditorPointer } from '@wix/editor-document-logic-api';

// TODO: get from cms-platform-integration package
const bindableComponentTypes = [
  'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_4220f51c-7462-49f6-7352-14219c165b28',
  'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_0bfa7aab-081b-431b-adb2-1b1062e8f21a',
  'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_4190f51c-3836-49f6-8114-96219c165b29',
  'wixEditorElements.Button',
  'wixEditorElements.Image',
  'wixEditorElements.WRichText',
  'wysiwyg.viewer.components.WRichText',
  'wysiwyg.viewer.components.Repeater',
];

interface BindingConfig<T = unknown> {
  type: 'binding';
  byItem: Record<string, T>;
}

export class BindingsPlatformService extends AbstractComponentsPlatformService {
  private readonly bindingType = 'binding';

  private isValidBindingConfig<T = unknown>(
    config: BindingConfig<T>,
  ): config is BindingConfig<T> {
    return (
      config &&
      typeof config === 'object' &&
      config.type === this.bindingType &&
      config.byItem &&
      typeof config.byItem === 'object'
    );
  }
  private getAllAvailableControllers(compRef: CompRef): AppController[] {
    const scopedControllers =
      this.dm.platform.controllers.getAvailableScopedControllers(compRef);
    const cmsControllers =
      this.dm.platform.controllers.getControllerDataPointersByAppDefId(
        'dataBinding',
      ) as AppController[];

    return scopedControllers.concat(cmsControllers);
  }

  private createEmptyConnection(
    role: string,
    controllerRef: AppControllerRef,
  ): Connection {
    const config: BindingConfig = {
      type: this.bindingType,
      byItem: {},
    };
    return {
      role,
      controllerRef,
      config,
      isPrimary: true,
    };
  }

  private findDataConnection(
    editorPointer: EditorPointer<CompRef>,
  ): Connection | null {
    const documentPointer =
      this.editorPointerAPI.getDocumentPointer(editorPointer);
    const connections =
      this.dm.platform.controllers.connections.get(documentPointer);
    for (const connection of connections) {
      if (this.isValidBindingConfig(connection.config)) {
        return connection;
      }
    }

    return null;
  }

  private updateConnection<T = unknown>(
    editorPointer: EditorPointer<CompRef>,
    controllerRef: AppControllerRef,
    role: string,
    connectionConfig: BindingConfig<T>,
  ) {
    const documentPointer =
      this.editorPointerAPI.getDocumentPointer(editorPointer);
    this.dm.platform.controllers.connections.disconnect(
      documentPointer,
      controllerRef,
      role,
    );
    this.dm.platform.controllers.connections.connect(
      documentPointer,
      controllerRef,
      role,
      connectionConfig,
      true,
    );
  }

  create<T = unknown>(
    editorPointer: EditorPointer<CompRef>,
    controllerRef: AppControllerRef,
    options: {
      role: string;
      config: T;
      key: BindingKey;
    },
  ): T {
    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer) || [];
    const encodedKey = BindingKeyManager.encode(innerElementPath, options.key);

    const dataConnection =
      this.findDataConnection(editorPointer) ||
      this.createEmptyConnection(options.role, controllerRef);
    const connectionConfig = dataConnection.config;

    if (connectionConfig.byItem[encodedKey]) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.PlatformComponentsService,
        'Binding already exists',
      );
    }

    connectionConfig.byItem[encodedKey] = options.config;

    this.updateConnection(
      editorPointer,
      controllerRef,
      options.role,
      connectionConfig,
    );

    return connectionConfig.byItem[encodedKey];
  }

  delete(editorPointer: EditorPointer<CompRef>, key: BindingKey) {
    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer) || [];
    const encodedKey = BindingKeyManager.encode(innerElementPath, key);

    const dataConnection = this.findDataConnection(editorPointer);

    if (!dataConnection) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.PlatformComponentsService,
        'No binding connection found for component',
      );
    }

    const controllerConfig = dataConnection.config;

    if (!controllerConfig.byItem[encodedKey]) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.PlatformComponentsService,
        `Binding does not exist for key: ${encodedKey}`,
      );
    }

    // Delete the binding
    const remainingItems = { ...controllerConfig.byItem };
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete remainingItems[encodedKey];
    controllerConfig.byItem = remainingItems;

    if (Object.keys(remainingItems).length === 0) {
      this.dm.platform.controllers.connections.disconnect(
        this.editorPointerAPI.getDocumentPointer(editorPointer),
        dataConnection.controllerRef,
        dataConnection.role,
      );
    } else {
      this.updateConnection(
        editorPointer,
        dataConnection.controllerRef,
        dataConnection.role,
        controllerConfig,
      );
    }
  }

  update<T = unknown>(
    editorPointer: EditorPointer<CompRef>,
    key: BindingKey,
    {
      config,
      role,
    }: {
      config: T;
      role?: string;
    },
  ): T {
    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer) || [];

    // Encode the BindingKey (which may include subItemId)
    const encodedKey = BindingKeyManager.encode(innerElementPath, key);

    const dataConnection = this.findDataConnection(editorPointer);

    if (!dataConnection) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.PlatformComponentsService,
        'No binding connection found for component',
      );
    }

    if (!this.isValidBindingConfig<T>(dataConnection.config)) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.PlatformComponentsService,
        'Invalid binding connection config',
      );
    }
    const connectionConfig = dataConnection.config;

    if (!connectionConfig.byItem[encodedKey]) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.PlatformComponentsService,
        `Binding does not exist for key: ${encodedKey}`,
      );
    }

    connectionConfig.byItem[encodedKey] = config;

    this.updateConnection(
      editorPointer,
      dataConnection.controllerRef,
      role || dataConnection.role,
      connectionConfig,
    );

    return connectionConfig.byItem[encodedKey];
  }

  get<T = unknown>(
    editorPointer: EditorPointer<CompRef>,
    key: BindingKey,
  ): T | null {
    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer) || [];

    // Encode the BindingKey (which may include subItemId)
    const encodedKey = BindingKeyManager.encode(innerElementPath, key);

    const dataConnection = this.findDataConnection(editorPointer);

    if (!dataConnection) {
      return null;
    }

    const connectionConfig = dataConnection.config;

    return connectionConfig.byItem[encodedKey] || null;
  }

  isBindable(editorPointer: EditorPointer<CompRef>): boolean {
    return this.getBindableControllers(editorPointer).length > 0;
  }

  getBindableControllers(
    editorPointer: EditorPointer<CompRef>,
  ): AppControllerRef[] {
    const documentPointer =
      this.editorPointerAPI.getDocumentPointer(editorPointer);
    const allControllers = this.getAllAvailableControllers(documentPointer);
    const componentType = this.dm.components.getType(documentPointer);

    return allControllers
      .filter(
        (controller) =>
          bindableComponentTypes.includes(componentType) &&
          controller.type === 'platform.components.AppController',
      )
      .map((controller) =>
        this.dm.platform.controllers.getAppControllerRef(controller),
      );
  }

  openDataBindingPanel(id: string) {
    return this.hostAPI.platformApiMethods.editor.openPropertyBindingPanel?.({
      dataItemId: id,
    });
  }

  private isDataBindingController(componentRef: CompRef): boolean {
    const compData = this.dm.components.data.get(componentRef);
    return compData?.applicationId === 'dataBinding';
  }

  hasDataBindingControllerInContext(
    editorPointer: EditorPointer<CompRef>,
  ): boolean {
    const documentPointer =
      this.editorPointerAPI.getDocumentPointer(editorPointer);

    const allControllers = this.getAllAvailableControllers(documentPointer);

    const hasScopedController = allControllers.some((controller) => {
      return controller?.applicationId === 'dataBinding';
    });

    const currentPage = this.dm.pages.getCurrentPage();

    const currentPageChildren = this.dm.components.getChildren(currentPage);

    const hasPageController = currentPageChildren.some((child) =>
      this.isDataBindingController(child),
    );

    return hasScopedController || hasPageController;
  }
}
